import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for Text-to-Speech using the browser's Web Speech API
 * No API key required - works directly in the browser
 */
export function useTextToSpeech() {
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [voices, setVoices] = useState([]);
  const utteranceRef = useRef(null);
  
  // Check support synchronously (outside effect)
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => {
    if (!supported) return;
    
    // Load available voices
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
    };

    // Voices might load async
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.cancel();
    };
  }, [supported]);

  /**
   * Get the best voice for a given language
   */
  const getBestVoice = useCallback((lang = 'en') => {
    if (!voices.length) return null;

    // Prefer these voices for better quality
    const preferredVoices = {
      en: ['Google US English', 'Microsoft Zira', 'Samantha', 'Alex'],
      es: ['Google español', 'Microsoft Sabina', 'Paulina', 'Jorge']
    };

    const langCode = lang === 'es' ? 'es' : 'en';
    const preferred = preferredVoices[langCode] || [];

    // Try to find a preferred voice
    for (const name of preferred) {
      const voice = voices.find(v => v.name.includes(name));
      if (voice) return voice;
    }

    // Fallback to any voice matching the language
    const langVoices = voices.filter(v => v.lang.startsWith(langCode));
    return langVoices[0] || voices[0];
  }, [voices]);

  /**
   * Speak text aloud
   */
  const speak = useCallback((text, options = {}) => {
    if (!supported || !text) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    // Apply options
    utterance.rate = options.rate || 1;
    utterance.pitch = options.pitch || 1;
    utterance.volume = options.volume || 1;

    // Set voice
    const voice = options.voice || getBestVoice(options.lang);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    }

    // Event handlers
    utterance.onstart = () => {
      setSpeaking(true);
      setPaused(false);
    };

    utterance.onend = () => {
      setSpeaking(false);
      setPaused(false);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error);
      setSpeaking(false);
      setPaused(false);
    };

    utterance.onpause = () => setPaused(true);
    utterance.onresume = () => setPaused(false);

    window.speechSynthesis.speak(utterance);
  }, [supported, getBestVoice]);

  /**
   * Pause speech
   */
  const pause = useCallback(() => {
    if (supported && speaking) {
      window.speechSynthesis.pause();
      setPaused(true);
    }
  }, [supported, speaking]);

  /**
   * Resume speech
   */
  const resume = useCallback(() => {
    if (supported && paused) {
      window.speechSynthesis.resume();
      setPaused(false);
    }
  }, [supported, paused]);

  /**
   * Stop speech
   */
  const stop = useCallback(() => {
    if (supported) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      setPaused(false);
    }
  }, [supported]);

  /**
   * Toggle play/pause
   */
  const toggle = useCallback(() => {
    if (paused) {
      resume();
    } else if (speaking) {
      pause();
    }
  }, [paused, speaking, pause, resume]);

  return {
    speak,
    pause,
    resume,
    stop,
    toggle,
    speaking,
    paused,
    supported,
    voices,
    getBestVoice
  };
}

/**
 * Strip HTML tags from text for TTS
 */
export function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export default useTextToSpeech;
